import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Lock, User, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(identifier, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-primary text-primary-foreground p-14 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-60" />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <span className="font-display text-xl font-bold tracking-tight">Veloce</span>
        </div>
        <div className="relative z-10 space-y-6">
          <p className="text-xs uppercase tracking-[0.18em] text-white/50">AI Sales Assistant Platform</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[1.05]">
            Satu dashboard.<br />Banyak bisnis.<br />
            <span className="text-accent">Asisten AI</span> di setiap toko.
          </h1>
          <p className="text-white/60 max-w-md text-base leading-relaxed">
            Hubungkan database, atur chatbot per bisnis, dan pasang widget penjualan cerdas di website manapun.
          </p>
        </div>
        <div className="relative z-10 flex gap-8 text-white/50 text-sm">
          <div><span className="block text-2xl font-display font-bold text-white">3</span>Database engine</div>
          <div><span className="block text-2xl font-display font-bold text-white">∞</span>Bisnis</div>
          <div><span className="block text-2xl font-display font-bold text-white">1</span>Embed script</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-lg font-bold">Veloce</span>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight">Masuk ke Studio</h2>
          <p className="text-muted-foreground mt-2 mb-8 text-sm">Kelola AI Sales Assistant untuk semua bisnismu.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="identifier">Email</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="identifier" data-testid="login-email-input" type="text" value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" data-testid="login-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="pl-10" required />
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full h-11 group bg-accent hover:bg-accent/90 text-white">
              {loading ? "Memproses..." : "Masuk"}
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
